import Button from '@/components/Button';
import Coupon from '@/components/Coupon/coupon';
import { couponDateTransform } from '@/components/Coupon/handler';
import Popup from '@/components/Popup';
import { useStore } from '@/providers/app-store-provider';

import Image from '@/components/Image';
import { ECouponUIType } from '@/utils/enums/coupon';
import { env } from '@/utils/env';
import { Box, IconButton, OutlinedInput } from '@mui/material';
import { InputDelete } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import { useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { IS_DE } from '@/utils/constants';

interface CouponListMProps {
  show: boolean;
  onClose: () => void;
  couponCode: string;
  onChangeCouponCode: (code: string) => void;
  checkCoupon: boolean;
  applyLoading: boolean;
  handleCodeApply: () => void;
  usableCouponList: any[];
  handleCouponApply: (item: any) => void;
  ref: any;
}

export default function CouponListM({
  show,
  onClose,
  couponCode,
  onChangeCouponCode,
  checkCoupon,
  applyLoading,
  handleCodeApply,
  usableCouponList,
  handleCouponApply,
  ref,
}: CouponListMProps) {
  // 能够修改isSelectedId 的状态
  useImperativeHandle(ref, () => ({
    setIsSelectedId,
  }));

  const [showTips, setShowTips] = useState(true);
  const currencyList = useStore((state) => state.currencyList);
  const currency = currencyList.find((item) => item.name === env.NEXT_PUBLIC_DEFAULT_CURRENCY);

  const t = useTranslations('coupons');

  const canUseCouponList = useMemo(() => {
    return usableCouponList.filter((item) => item.canuse);
  }, [usableCouponList]);

  const canNotUseCouponList = useMemo(() => {
    return usableCouponList.filter((item) => !item.canuse);
  }, [usableCouponList]);

  const handledCanUseCouponList = useMemo(() => {
    return canUseCouponList.map((item) => couponDateTransform(item, false, t, currency, IS_DE));
  }, [canUseCouponList]);

  const handledCanNotUseCouponList = useMemo(() => {
    return canNotUseCouponList.map((item) => {
      const transformed = couponDateTransform(item, false, t, currency, IS_DE);
      return {
        ...transformed,
        err_msg: item.err_msg,
      };
    });
  }, [canNotUseCouponList]);

  const [isSelectedId, setIsSelectedId] = useState<number | null>(null);

  const handleCouponClick = (item: any) => {
    handleCouponApply(item.paymentParams);
    setIsSelectedId(item.paymentParams.id);
  };

  useEffect(() => {
    if (show) {
      setIsSelectedId(null);
    }
  }, [show]);

  return (
    <Popup
      open={show}
      onClose={onClose}
      position="bottom"
      height="76vh"
      title={t('Coupons')}
      sx={{
        '& .popup-content': { p: 0 },
        '& .popup-title-text': { fontSize: '18px', fontFamily: 'var(--font-bricolage-bold)' },
      }}
    >
      <div>
        {showTips && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: '40px',
              padding: '0 12px',
              boxSizing: 'border-box',
              backgroundColor: '#FDF6DC',
            }}
          >
            <div className="text-[12px]">{t('onlyOneCouponPerOrder')} </div>
            <IconButton onClick={() => setShowTips(false)} size="small" sx={{ color: '#BBBBBB' }}>
              <InputDelete />
            </IconButton>
          </Box>
        )}

        <div className="px-14 pb-30">
          {/* input */}
          <div className="flex h-[40px] mt-16 relative">
            <OutlinedInput
              className="w-full "
              placeholder={t('enterACouponCode')}
              value={couponCode}
              onChange={(e) => onChangeCouponCode(e.target.value)}
              size="small"
              sx={{
                // 设置固定高度为40px
                height: '40px',
                minHeight: '40px',
                maxHeight: '40px',
                borderRadius: '30px',
                backgroundColor: '#F5F5F5',
                border: 'none',
                'font-size': '13px',
                '& .MuiOutlinedInput-input': {
                  height: '40px',
                  padding: '0 16px',
                  lineHeight: '40px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            />
            <div className="absolute right-0">
              <Button
                variant="contained"
                color="black"
                onClick={() => {
                  handleCodeApply();
                }}
                loading={applyLoading}
                sx={{
                  height: '40px',
                  borderRadius: '30px',
                  width: '90px',
                  fontSize: '16px',
                  fontFamily: 'var(--font-bricolage-bold)',
                }}
              >
                {checkCoupon ? t('Cancel') : t('Apply')}
              </Button>
            </div>
          </div>

          {/* coupon list */}
          <div className=" mt-36">
            {usableCouponList && usableCouponList.length > 0 ? (
              <div className="flex flex-col gap-10 ">
                {handledCanUseCouponList.map((item, index) => (
                  <div
                    key={`${item.id}-${index}`}
                    className="cursor-pointer"
                    onClick={() => handleCouponClick(item)}
                  >
                    <Coupon
                      id={item.id}
                      couponType={item.couponType}
                      couponUIType={item.couponUIType}
                      disCountText={item.disCountText}
                      title={item.title}
                      description={item.description}
                      buttonText={item.buttonText}
                      copyWriting={item.copyWriting}
                      discountTextClassName={'!text-18 !leading-18'}
                      titleClassName={'!text-14 !leading-14'}
                      containerClassName={'!px-12 !py-18'}
                      extension_field={item?.extension_field || {}}
                      renderRightButton={() => (
                        <div className="flex items-center justify-center">
                          <Image
                            src={
                              isSelectedId === item.paymentParams.id
                                ? '/payment/select.svg'
                                : '/payment/not-select.svg'
                            }
                            alt="check"
                            width={20}
                            height={20}
                          />
                        </div>
                      )}
                    />
                  </div>
                ))}

                {handledCanNotUseCouponList && handledCanNotUseCouponList.length > 0 && (
                  <div className="text-14 ">{t('Unavailable')}</div>
                )}

                {handledCanNotUseCouponList.map((item) => (
                  <div key={item.id}>
                    <Coupon
                      id={item.id}
                      couponType={item.couponType}
                      couponUIType={ECouponUIType.DISABLED}
                      disCountText={item.disCountText}
                      title={item.title}
                      description={item.description}
                      buttonText={item.buttonText}
                      copyWriting={item.copyWriting}
                      errorMessage={item.err_msg}
                      discountTextClassName={'!text-18 !leading-18'}
                      titleClassName={'!text-14 !leading-14'}
                      containerClassName={'!pt-12 !pb-2'}
                      extension_field={item?.extension_field || {}}
                      renderRightButton={() => (
                        <div className="flex items-center justify-center">
                          <Image
                            src={'/payment/not-select.svg'}
                            alt="check"
                            width={20}
                            height={20}
                          />
                        </div>
                      )}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <Image src="/payment/no-coupons.png" alt="no-coupon" width={100} height={100} />
                <div className="text-center text-14 w-full h-[60px] leading-[60px] flex items-center justify-center mt-10">
                  {t('noCoupon')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Popup>
  );
}
